import React, { useEffect, useMemo, useState } from "react";
import AuthService from "../services/authService";
import Spinner from "./Spinner";
import Fuse from "fuse.js";

interface TeamSport {
  id: number;
  name: string;
  code: string;
  g_league_id?: number | null;
}

interface Team {
  id: number;
  team_name: string;
  team_code?: number;
  img_url?: string | null;
  sports?: TeamSport[];
}

const TeamList: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedSports, setSelectedSports] = useState<string[]>([]);

  const authService = new AuthService();

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await authService.getAdminTeams();
      if (response.status === 200) {
        const payload = response.data;
        const teamsData = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.teams)
            ? payload.teams
            : [];
        setTeams(teamsData);
      } else {
        setError("Failed to load teams");
      }
    } catch (err) {
      setError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const sportCodes = useMemo(() => {
    const codes = new Set<string>();
    for (const team of teams) {
      for (const sport of team.sports ?? []) {
        if (sport.code) codes.add(sport.code);
      }
    }
    return Array.from(codes).sort();
  }, [teams]);

  const fuse = useMemo(
    () =>
      new Fuse(teams, {
        keys: ["team_name"],
        threshold: 0.2,
      }),
    [teams]
  );

  const filteredTeams = useMemo(() => {
    let result = search ? fuse.search(search).map((r) => r.item) : teams;

    if (selectedSports.length > 0) {
      const selected = new Set(selectedSports);
      result = result.filter((team) =>
        (team.sports ?? []).some((sport) => selected.has(sport.code))
      );
    }

    return result;
  }, [teams, fuse, search, selectedSports]);

  const toggleSport = (code: string) => {
    setSelectedSports((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <p className="text-red-600">{error}</p>;
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Team List</h2>

        <input
          type="text"
          placeholder="Search teams..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {sportCodes.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-600 mr-1">Sports:</span>
          {sportCodes.map((code) => {
            const isSelected = selectedSports.includes(code);
            return (
              <button
                key={code}
                type="button"
                onClick={() => toggleSport(code)}
                className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium border transition ${
                  isSelected
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {code}
              </button>
            );
          })}
          {selectedSports.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectedSports([])}
              className="text-xs text-blue-600 hover:underline ml-1"
            >
              Clear
            </button>
          )}
        </div>
      )}

      <p className="text-sm text-gray-500 mb-2">
        {filteredTeams.length} of {teams.length} teams
      </p>

      <div className="divide-y divide-gray-200">
        {filteredTeams.length === 0 ? (
          <p className="py-6 text-sm text-gray-500">No teams match your filters.</p>
        ) : (
          filteredTeams.map((team) => (
            <div
              key={team.id}
              className="flex items-center gap-4 py-3 hover:bg-gray-50 transition"
            >
              <div className="w-12 h-12 flex-shrink-0">
                {team.img_url ? (
                  <img
                    src={team.img_url}
                    alt={team.team_name}
                    className="w-12 h-12 object-contain"
                  />
                ) : (
                  <div className="w-12 h-12 bg-gray-200 flex items-center justify-center rounded text-xs text-gray-500">
                    No Logo
                  </div>
                )}
              </div>

              <div className="font-semibold text-gray-800 min-w-[180px]">
                {team.team_name}
              </div>

              <div className="flex flex-wrap gap-1.5">
                {(team.sports ?? []).length === 0 ? (
                  <span className="text-sm text-gray-400">—</span>
                ) : (
                  (team.sports ?? []).map((sport) => (
                    <span
                      key={sport.code}
                      className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        selectedSports.includes(sport.code)
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {sport.code}
                    </span>
                  ))
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TeamList;
